/**
 * AdminSnapshots — Centralized view of all runner + agent snapshots
 * Dark glassmorphism design — NO EMOJIS
 */
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { SkeletonBar, Button } from '../components/ui';
import { T } from '../styles/tokens';

interface RunnerData {
  os: string | null;
  arch: string | null;
  version: string | null;
  node: string | null;
  docker: string | null;
  has_docker: number | null;
  openclaw: string | null;
  status: string | null;
  heartbeat: string | null;
  mode: string | null;
}

interface AgentData {
  name: string | null;
  model: string | null;
  provider: string | null;
  language: string | null;
  brain: string | null;
  skills: string | null;
  channels: string | null;
  integrations: string | null;
  env_keys: string | null;
  tools: string | null;
  synced_at: string | null;
  agents_md: string | null;
  heartbeat_md: string | null;
  identity_md: string | null;
  bootstrap_md: string | null;
  openclaw_version: string | null;
  oc_path: string | null;
  env_path: string | null;
  config_path: string | null;
  config_json: string | null;
}

interface BrainData {
  soul_md: string | null;
  user_md: string | null;
  memory_md: string | null;
  tools_md: string | null;
  snapshot_at: string | null;
}

interface Snapshot {
  id: string;
  display_name: string;
  hostname: string | null;
  customer_email: string | null;
  provider: string;
  freshness: 'fresh' | 'stale' | 'missing';
  runner_data: RunnerData;
  agent_data: AgentData | null;
  brain_data: BrainData | null;
  credentials: Record<string, string[]> | null;
  custom_providers: { name: string; baseUrl: string; model?: string }[] | null;
}

interface Metrics {
  total: number;
  fresh: number;
  stale: number;
  missing: number;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '--';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'agora';
  if (diff < 60_000) return `${Math.round(diff / 1000)}s`;
  if (diff < 3600_000) return `${Math.round(diff / 60000)}min`;
  if (diff < 86400_000) return `${Math.round(diff / 3600000)}h`;
  return `${Math.floor(diff / 86400000)}d`;
}

function freshnessColor(f: string): string {
  if (f === 'fresh') return T.success;
  if (f === 'stale') return T.warning;
  return T.danger;
}

function freshnessLabel(f: string): string {
  if (f === 'fresh') return 'Atualizado';
  if (f === 'stale') return 'Desatualizado';
  return 'Sem snapshot';
}

function parseList(val: string | null): string[] {
  if (!val) return [];
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return val.split(',').map(s => s.trim()).filter(Boolean);
  }
}

/* ═══ Sub-components ═══ */

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: '16px 20px', flex: '1 1 140px', minWidth: 140,
    }}>
      <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: 'Manrope, sans-serif' }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(30,41,59,0.5)' }}>
      <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 12, color: T.text, fontWeight: 600, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '--'}</span>
    </div>
  );
}

function Chip({ text, color = T.primary }: { text: string; color?: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      fontSize: 10, fontWeight: 600, color,
      background: `${color}15`, border: `1px solid ${color}30`,
      marginRight: 4, marginBottom: 4,
    }}>{text}</span>
  );
}

function ChipList({ items, color, label }: { items: string[]; color?: string; label: string }) {
  if (!items.length) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 500, marginBottom: 3 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {items.map(item => <Chip key={item} text={item} color={color} />)}
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string | null }) {
  const color = status === 'online' ? T.success : status === 'offline' ? T.danger : T.warning;
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: color, marginRight: 6, flexShrink: 0,
      animation: status === 'online' ? 'pulse 2s infinite' : 'none',
    }} />
  );
}

function FreshnessBadge({ freshness }: { freshness: string }) {
  const color = freshnessColor(freshness);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, color,
      background: `${color}15`, border: `1px solid ${color}30`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {freshnessLabel(freshness)}
    </span>
  );
}

function PresenceDot({ present }: { present: boolean }) {
  return (
    <span style={{
      display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
      background: present ? T.success : T.danger, marginRight: 6, flexShrink: 0,
    }} />
  );
}

function FileEntry({ name, content, expanded, onToggle }: { name: string; content: string | null; expanded: boolean; onToggle: () => void }) {
  const present = !!content;
  const charCount = content ? content.length : 0;
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <PresenceDot present={present} />
          <span style={{ fontSize: 11, color: present ? T.text : T.textMuted, fontWeight: 600 }}>{name}</span>
          {present && <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 4 }}>({charCount.toLocaleString()} chars)</span>}
        </div>
        {present && (
          <Button variant="ghost"
            onClick={onToggle}
            style={{
              background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 4,
              color: T.textMuted, fontSize: 10, fontWeight: 600, padding: '2px 8px',
              cursor: 'pointer',
            }}
          >{expanded ? 'Ocultar' : 'Expandir'}</Button>
        )}
      </div>
      {expanded && content && (
        <div style={{
          background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6,
          padding: 8, marginTop: 4, maxHeight: 150, overflow: 'auto',
        }}>
          <pre style={{ fontSize: 10, color: T.textSecondary, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {content.length > 500 ? content.slice(0, 500) + '\n... (truncado)' : content}
          </pre>
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({ title, color, children, defaultOpen = false }: { title: string; color: string; children: any; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 8 }}>
      <Button variant="ghost"
        onClick={() => setOpen(!open)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', width: '100%',
        }}
      >
        <span style={{ fontSize: 10, color: T.textMuted, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0)' }}></span>
        <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
      </Button>
      {open && <div style={{ paddingLeft: 16, paddingTop: 4 }}>{children}</div>}
    </div>
  );
}

/* ═══ Snapshot Card ═══ */

function SnapshotCard({ snap }: { snap: Snapshot }) {
  const [expandedJson, setExpandedJson] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Record<string, boolean>>({});
  const rd = snap.runner_data;
  const ad = snap.agent_data;
  const bd = snap.brain_data;

  const toggleFile = (name: string) => setExpandedFiles(prev => ({ ...prev, [name]: !prev[name] }));

  const envKeys = parseList(ad?.env_keys || null);

  return (
    <div style={{
      background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 14,
      overflow: 'hidden', transition: 'all 0.2s',
    }}
      onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = `${T.primary}40`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <StatusDot status={rd.status} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: 'Manrope, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {snap.display_name}
            </div>
            {snap.customer_email && (
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{snap.customer_email}</div>
            )}
          </div>
        </div>
        <FreshnessBadge freshness={snap.freshness} />
      </div>

      {/* Body — 2 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 640 ? '1fr' : '1fr 1fr', gap: 0 }}>
        {/* Left: Runner */}
        <div style={{ padding: '14px 18px', borderRight: window.innerWidth < 640 ? 'none' : `1px solid ${T.border}`, borderBottom: window.innerWidth < 640 ? `1px solid ${T.border}` : 'none' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.primary, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>Runner</div>
          <InfoRow label="OS" value={rd.os} />
          <InfoRow label="Arch" value={rd.arch} />
          <InfoRow label="Versao" value={rd.version} />
          <InfoRow label="Node.js" value={rd.node} />
          <InfoRow label="Docker" value={rd.docker || (rd.has_docker ? 'sim' : 'nao')} />
          <InfoRow label="OpenClaw" value={rd.openclaw} />
          <InfoRow label="Status" value={rd.status} />
          <InfoRow label="Heartbeat" value={timeAgo(rd.heartbeat)} />
          <InfoRow label="Modo" value={rd.mode} />
        </div>

        {/* Right: Agent */}
        <div style={{ padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>Agente</div>
          {ad ? (<>
            <InfoRow label="Nome" value={ad.name} />
            <InfoRow label="Modelo" value={ad.model} />
            <InfoRow label="Provider" value={ad.provider} />
            <InfoRow label="Idioma" value={ad.language} />
            <InfoRow label="Brain" value={ad.brain} />
            <InfoRow label="Sincronizado" value={timeAgo(ad.synced_at)} />

            {/* Brain Files */}
            <CollapsibleSection title="Brain Files" color={T.success}>
              <FileEntry name="SOUL.md" content={bd?.soul_md || null} expanded={!!expandedFiles['soul']} onToggle={() => toggleFile('soul')} />
              <FileEntry name="USER.md" content={bd?.user_md || null} expanded={!!expandedFiles['user']} onToggle={() => toggleFile('user')} />
              <FileEntry name="MEMORY.md" content={bd?.memory_md || null} expanded={!!expandedFiles['memory']} onToggle={() => toggleFile('memory')} />
              <FileEntry name="TOOLS.md" content={bd?.tools_md || null} expanded={!!expandedFiles['tools']} onToggle={() => toggleFile('tools')} />
              {bd?.snapshot_at && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>Snapshot: {timeAgo(bd.snapshot_at)}</div>}
            </CollapsibleSection>

            {/* Workspace */}
            <CollapsibleSection title="Workspace" color={T.warning}>
              <FileEntry name="AGENTS.md" content={ad.agents_md} expanded={!!expandedFiles['agents']} onToggle={() => toggleFile('agents')} />
              <FileEntry name="HEARTBEAT.md" content={ad.heartbeat_md} expanded={!!expandedFiles['heartbeat']} onToggle={() => toggleFile('heartbeat')} />
              <FileEntry name="IDENTITY.md" content={ad.identity_md} expanded={!!expandedFiles['identity']} onToggle={() => toggleFile('identity')} />
              <FileEntry name="BOOTSTRAP.md" content={ad.bootstrap_md} expanded={!!expandedFiles['bootstrap']} onToggle={() => toggleFile('bootstrap')} />
            </CollapsibleSection>

            {/* Config */}
            <CollapsibleSection title="Config" color={T.primary}>
              <FileEntry name="openclaw.json" content={ad.config_json} expanded={!!expandedFiles['config']} onToggle={() => toggleFile('config')} />
            </CollapsibleSection>

            {/* Credentials — from machine_integrations (real encrypted keys) */}
            <CollapsibleSection title={`Credenciais${snap.credentials ? ` (${Object.keys(snap.credentials).length} providers)` : ''}`} color={T.warning}>
              {snap.credentials ? (
                <div>
                  {Object.entries(snap.credentials).map(([prov, keys]) => (
                    <div key={prov} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.success, flexShrink: 0 }} />
                        {prov}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', paddingLeft: 12 }}>
                        {keys.map(k => <Chip key={k} text={k} color={T.warning} />)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: T.textMuted, fontStyle: 'italic' }}>Nenhuma credencial salva</div>
              )}
            </CollapsibleSection>

            {/* Env Keys — from agent config */}
            {envKeys.length > 0 && (
              <CollapsibleSection title={`Env Keys (${envKeys.length})`} color={T.purple}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {envKeys.map(k => <Chip key={k} text={k} color={T.purple} />)}
                </div>
              </CollapsibleSection>
            )}

            {/* Custom LLM Providers */}
            {snap.custom_providers && snap.custom_providers.length > 0 && (
              <CollapsibleSection title={`Custom LLM Providers (${snap.custom_providers.length})`} color="#EC4899">
                {snap.custom_providers.map((p, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    <InfoRow label={p.name} value={p.baseUrl} />
                    {p.model && <InfoRow label="Modelo" value={p.model} />}
                  </div>
                ))}
              </CollapsibleSection>
            )}

            {/* System */}
            <CollapsibleSection title="Sistema" color={T.textMuted}>
              <InfoRow label="OpenClaw Version" value={ad.openclaw_version} />
              <InfoRow label="OC Path" value={ad.oc_path} />
              <InfoRow label="Env Path" value={ad.env_path} />
              <InfoRow label="Config Path" value={ad.config_path} />
            </CollapsibleSection>

            {/* Tools */}
            {parseList(ad.tools).length > 0 && (
              <CollapsibleSection title={`Tools (${parseList(ad.tools).length})`} color={T.textMuted}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {parseList(ad.tools).map(t => <Chip key={t} text={t} color={T.textMuted} />)}
                </div>
              </CollapsibleSection>
            )}

            {/* Skills/Channels/Integrations chips */}
            <div style={{ marginTop: 8 }}>
              <ChipList items={parseList(ad.skills)} label="Skills" color={T.success} />
              <ChipList items={parseList(ad.channels)} label="Channels" color={T.primary} />
              <ChipList items={parseList(ad.integrations)} label="Integracoes" color={T.purple} />
            </div>
          </>) : (
            <div style={{ color: T.textMuted, fontSize: 12, fontStyle: 'italic', padding: '20px 0', textAlign: 'center' }}>
              Nenhum dado de agente coletado
            </div>
          )}
        </div>
      </div>

      {/* Expand button */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: '8px 18px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="ghost"
          onClick={() => setExpandedJson(!expandedJson)}
          style={{
            background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 6,
            color: T.textMuted, fontSize: 11, fontWeight: 600, padding: '4px 12px',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.color = T.primary; }}
          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}
        >
          {expandedJson ? 'Ocultar JSON' : 'Ver JSON'}
        </Button>
      </div>

      {/* JSON raw */}
      {expandedJson && (
        <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 18px', background: T.bg }}>
          <pre style={{ fontSize: 11, color: T.textSecondary, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 300, overflow: 'auto' }}>
            {JSON.stringify(snap, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/* ═══ Main Page ═══ */

export function AdminSnapshots() {
  if (!isLoggedIn.value) return <LoginPage />;

  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [metrics, setMetrics] = useState<Metrics>({ total: 0, fresh: 0, stale: 0, missing: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'fresh' | 'stale' | 'missing'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/cloud/admin/snapshots') as any;
        const data = res?.data || res;
        setSnapshots(data.snapshots || []);
        setMetrics(data.metrics || { total: 0, fresh: 0, stale: 0, missing: 0 });
      } catch (e) {
        console.error('Failed to fetch snapshots:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = snapshots.filter(s => {
    if (filter !== 'all' && s.freshness !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (s.display_name?.toLowerCase().includes(q) ||
        s.hostname?.toLowerCase().includes(q) ||
        s.customer_email?.toLowerCase().includes(q) ||
        s.agent_data?.name?.toLowerCase().includes(q) ||
        s.agent_data?.model?.toLowerCase().includes(q));
    }
    return true;
  });

  const FilterBtn = ({ value, label, count }: { value: string; label: string; count: number }) => (
    <Button variant="ghost"
      onClick={() => setFilter(value as any)}
      style={{
        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.15s',
        background: filter === value ? T.primary : 'transparent',
        color: filter === value ? '#fff' : T.textMuted,
        border: filter === value ? `1px solid ${T.primary}` : `1px solid ${T.border}`,
      }}
    >
      {label} ({count})
    </Button>
  );

  return (
    <div style={{ padding: '16px 12px', maxWidth: 1400, margin: '0 auto', minHeight: '100vh', background: T.bg }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, fontFamily: 'Manrope, sans-serif', margin: '0 0 24px 0' }}>
        Snapshots
      </h1>

      {loading ? (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.borderSubtle}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>) : (<>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard label="Total" value={metrics.total} color="#e2e8f0" />
          <StatCard label="Atualizados" value={metrics.fresh} color={T.success} />
          <StatCard label="Desatualizados" value={metrics.stale} color={T.warning} />
          <StatCard label="Sem snapshot" value={metrics.missing} color={T.danger} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterBtn value="all" label="Todos" count={metrics.total} />
          <FilterBtn value="fresh" label="Atualizados" count={metrics.fresh} />
          <FilterBtn value="stale" label="Desatualizados" count={metrics.stale} />
          <FilterBtn value="missing" label="Sem snapshot" count={metrics.missing} />
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onInput={(e: any) => setSearch(e.target.value)}
            style={{
              marginLeft: 'auto', padding: '7px 14px', borderRadius: 8,
              background: T.bgInput, border: `1px solid ${T.border}`, color: T.text,
              fontSize: 13, outline: 'none', minWidth: 140, flex: '1 1 140px',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 560px), 1fr))', gap: 16 }}>
          {filtered.map(snap => <SnapshotCard key={snap.id} snap={snap} />)}
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: T.textMuted, fontSize: 14 }}>
            Nenhum snapshot encontrado
          </div>
        )}
      </>)}

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}
